<template>
  <div class="w-100 position-relative mb-1">
    <SCheckbox
      v-if="isMultipleAnswerable || isQuestionTypeFreeText"
      v-model="localOption.is_chosen"
      :data-cy="localOption.has_user_option ? 'sdui-survey-option-select-user-option' : 'sdui-survey-option-select'"
      :label-text="localOption.name"
      name="is_chosen"
      :disabled="disabled"
      checkbox-type="square"
      class="survey-answer-option"
      :class="{ active: isActive, 'with-results': showResults }"
      @change="optionChanged"
    >
      <template #additional-sr-content>
        <span v-if="disabled"
              class="sr-only"
        >{{ option.name }}, {{ $t('survey.options.already_voted_sr', { numOfVotes: option.count, percentage: option.percentage }) }}</span>
      </template>
      <template #additional-content>
        <sdui-survey-option-content
          :option="localOption"
          :is-active="isActive"
          :show-results="showResults"
          :is-question-type-free-text="isQuestionTypeFreeText"
          :is-multiple-answerable="isMultipleAnswerable"
          :disabled="disabled"
          :user-option-changed="userOptionChanged"
        />
      </template>
    </SCheckbox>
    <SRadioButton
      v-else
      :data-cy="localOption.has_user_option ? 'sdui-survey-option-select-user-option' : 'sdui-survey-option-select'"
      :currently-selected-value="selectedOptionValues[0]"
      :value-on-selection="localOption.uuid"
      :label-text="localOption.name"
      :disabled="disabled"
      :name="String(radioBtnGroupPrefix)"
      radio-button-type="round"
      class="survey-answer-option"
      :class="{ active: isActive, 'with-results': showResults && !isQuestionTypeFreeText }"
      @change="optionChanged"
      @click="selectedOptionValues[0] === localOption.uuid ? optionChanged() : undefined"
    >
      <template #additional-sr-content>
        <span v-if="disabled"
              class="sr-only"
        >{{ option.name }}, {{ $t('survey.options.already_voted_sr', { numOfVotes: option.count, percentage: option.percentage }) }}</span>
      </template>
      <template #additional-content>
        <sdui-survey-option-content
          :option="localOption"
          :is-active="isActive"
          :show-results="showResults"
          :is-question-type-free-text="isQuestionTypeFreeText"
          :is-multiple-answerable="isMultipleAnswerable"
          :disabled="disabled"
          :user-option-changed="userOptionChanged"
        />
      </template>
    </SRadioButton>

    <SModal
      v-model="modals.userOption"
      centered
      no-close-on-backdrop
      :header-title="$t('survey.free_text.modal.title')"
      :show-footer-gradient="false"
      @hidden="resetModal"
      @hide="resetModal"
      @ok="handleOk"
      @show="initialOpen"
      @close="cancelOption"
    >
      <VForm
        v-if="modals.userOption"
        id="form-sdui-survey-option-modal-user-option"
        :initial-values="initialFormValues"
        @submit="handleSubmit"
      >
        <p class="text-muted">
          {{ $t('survey.free_text.modal.visibility_hint') }}
        </p>
        <SInput
          v-model="nameForSurvey"
          :validation-rules="`required|max:${maxCharacters}`"
          validation-field-name="value"
          :label-text="$t('survey.free_text.modal.placeholder')"
          hide-label
          :placeholder="$t('survey.free_text.modal.placeholder')"
          required
          data-cy="sdui-survey-option-input"
          size="lg"
          test-id="create-survey-option-input-error"
        />
      </VForm>
      <template #modal-footer>
        <b-button
          block
          size="lg"
          variant="primary"
          data-cy="survey-option-submit"
          type="submit"
          form="form-sdui-survey-option-modal-user-option"
          class="no-spinner"
        >
          {{ $t('survey.free_text.modal.submit') }}
        </b-button>
      </template>
    </SModal>
  </div>
</template>

<script>
import constants from 'src/config/constants.ts'
import SRadioButton from 'src/components/base/SRadioButton.vue'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import SduiSurveyOptionContent from 'src/components/miscellaneous/ui/surveys/SduiSurveyOptionContent.vue'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'SduiSurveyOption',

  components: { SCheckbox, SRadioButton, SduiSurveyOptionContent },

  props: {
    option: {
      type: Object,
      default: () => ({}),
    },

    isMultipleAnswerable: {
      type: Boolean,
      default: false,
    },

    isQuestionTypeFreeText: {
      type: Boolean,
      default: false,
    },

    isSurveyPublicRelease: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: Array,
      default: () => [],
    },

    name: {
      type: Number,
      default: 0,
    },

    showResults: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    inputPrefix: {
      type: String,
      default: 'option',
    },

    radioBtnGroupPrefix: {
      type: [String, Number],
      default: '',
    },
  },

  data: () => ({
    maxCharacters: constants.survey.maxCharacters,
    nameForSurvey: '',
    userOptionChanged: false,
    localOption: {},
    modals: { userOption: false },
  }),

  computed: {
    isCurrentOptionSelected() {
      return this.selectedOptionValues && !!this.selectedOptionValues.find((s) => s === this.localOption.uuid)
    },

    isActive() {
      return this.isCurrentOptionSelected || this.localOption.is_chosen
    },

    selectedOptionValues: {
      get() {
        return this.modelValue
      },

      set() {
        this.$emit('change', this.localOption.uuid)
      },
    },

    initialFormValues() {
      return {
        value: this.nameForSurvey,
      }
    },
  },

  emits: ['change', 'cancelOption', 'validateOption'],

  methods: {
    optionChanged() {
      if (this.localOption.has_user_option && !(this.modelValue && this.modelValue.find((el) => el === this.localOption.uuid))) {
        this.modals.userOption = true
      }

      this.selectedOptionValues = this.localOption.uuid
    },

    resetModal() {
      this.$emit('validateOption', { answer: this.nameForSurvey }, this.localOption)
      this.modals.userOption = false
    },

    initialOpen() {
      if (this.localOption.name === this.$t('survey.create.free_text.survey_placeholder')) return
      this.nameForSurvey = this.localOption.name
    },

    async cancelOption() {
      this.$emit('cancelOption', { answer: this.nameForSurvey, option: this.localOption })
      await this.$nextTick()
      this.nameForSurvey = ''
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleSubmit()
    },

    async handleSubmit() {
      this.localOption.name = this.nameForSurvey
      this.userOptionChanged = true
      await this.$nextTick()
      this.modals.userOption = false
    },
  },

  watch: {
    option: {
      handler: function (value) {
        if (!value || !Object.keys(value).length) return
        this.localOption = this.$clone(value)
      },

      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.survey-answer-option {
  display: block !important;
  font-size: pxtorem(15);

  :deep(.additional-content) {
    flex-direction: row-reverse;
    justify-content: start;
    border-radius: 8px;
    min-height: 44px;
    padding: 6px 12px;
    opacity: 1;
    position: relative;

    .question-text-wrapper,
    .question-right-wrapper {
      z-index: 1;
    }

    &.disabled {
      filter: none;
      opacity: 1;
    }
  }

  :deep(.round-radio-button),
  :deep(.square-checkbox) {
    margin-right: 12px;
  }

  :deep(input[type='radio']:disabled),
  :deep(input[type='checkbox']:disabled) {
    display: none;
  }

  &.active {
    :deep(.additional-content) {
      color: $color-primary;
      border: 1px solid $color-primary;
      background-color: $color-primary-light;
    }

    .primary-when-active {
      color: $color-primary;
    }

    &.with-results {
      :deep(.additional-content) {
        background: transparent;
      }

      :deep(.survey-option-progress .s-progress-bar) {
        background-color: $color-primary-light !important;
      }
    }
  }

  :deep(input:focus-visible + :is(.additional-content)) {
    color: inherit !important;
  }
}
</style>
